import React from 'react'
import { Grid, Box, Typography, Checkbox } from '@mui/material'
import * as yup from 'yup'
import { useFormik } from 'formik'
import RoundedTextField from 'src/components/shared/atoms/roundInputField_v3'
import { ButtonComponent } from 'src/components/shared/atoms'
import TextArea from 'src/components/shared/atoms/textAreaField'
import { useSnackbar } from 'notistack'
import { useVolunteerMutation } from 'src/store/ApiCall/volunteerAPI'

import useStyles from './Styles'
import { DonateSkillFormI } from 'src/types/store'
import { getCookie } from 'cookies-next'

interface IProps {
  content: DonateSkillFormI
}
const VolunteerForm: React.FC<IProps> = ({ content }) => {
  const { enqueueSnackbar } = useSnackbar()
  const lang = getCookie('lang')
  const validationSchema = yup.object({
    name: yup
      .string()
      .required(lang === 'En' ? 'Name is required' : 'Se requiere el nombre')
      .lowercase(),
    location: yup
      .string()
      .required(
        lang === 'En' ? 'Location is required' : 'Se requiere ubicación'
      )
      .lowercase()
      .trim(),
    email: yup
      .string()
      .email(
        lang === 'En'
          ? 'Enter a valid email'
          : 'Introduzca un correo electrónico válido'
      )
      .required(
        lang === 'En' ? 'Email is required' : 'correo electronico es requerido'
      ),
    skills: yup
      .string()
      .required(
        lang === 'En'
          ? 'Skills field is required'
          : 'El campo de habilidades es obligatorio'
      ),
    volunteerDuration: yup.string(),
    portfolio: yup.string(),
    additionalInformation: yup.string(),
    storeInformation: yup.boolean(),
  })
  const [volunteer] = useVolunteerMutation()

  const formik = useFormik({
    initialValues: {
      name: '',
      location: '',
      email: '',
      skills: '',
      volunteerDuration: '',
      portfolio: '',
      additionalInformation: '',
      storeInformation: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const res = await volunteer({
        data: {
          name: values.name,
          location: values.location,
          email: values.email,
          skills: values.skills,
          volunteerDuration: values.volunteerDuration,
          portfolio: values.portfolio,
          additionalInfo: values.additionalInformation,
          storeInfo: values.storeInformation,
        },
      })

      if (res) {
        enqueueSnackbar('Your application is submitted!', {
          variant: 'success',
        })
      } else {
        enqueueSnackbar('Error processing application, please try again', {
          variant: 'error',
        })
      }
    },
  })

  const { classes } = useStyles()
  return (
    <div className={classes.formWrapper}>
      <Typography className={classes.formTitle}>
        {content.formHeading}
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container columnSpacing={{ lg: 8, md: 8, sm: 6, xs: 0 }}>
          <Grid item md={6} sm={12} xs={12}>
            {' '}
            <RoundedTextField
              id="name"
              label={content.namePlaceholder}
              name="name"
              placeHolder={content.namePlaceholder}
              value={formik.values.name}
              onChangeHandler={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid item md={6} sm={12} xs={12}>
            {' '}
            <RoundedTextField
              label={content.locationPlaceholder}
              id="location"
              name="location"
              placeHolder={content.locationPlaceholder}
              value={formik.values.location}
              onChangeHandler={formik.handleChange}
              error={formik.touched.location && Boolean(formik.errors.location)}
              helperText={formik.touched.location && formik.errors.location}
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <RoundedTextField
              label={content.emailPlaceholder}
              id="email"
              name="email"
              placeHolder={content.emailPlaceholder}
              value={formik.values.email}
              onChangeHandler={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>
          <Grid item md={12} sm={12} xs={12}>
            <RoundedTextField
              label={content.skillsYouDonatePlaceholder}
              id="skills"
              name="skills"
              placeHolder={content.skillsYouDonatePlaceholder}
              value={formik.values.skills}
              onChangeHandler={formik.handleChange}
              error={formik.touched.skills && Boolean(formik.errors.skills)}
              helperText={formik.touched.skills && formik.errors.skills}
            />
          </Grid>
          <Grid item md={12} sm={12} xs={12}>
            <RoundedTextField
              label={content.howMuchTimePlaceholder}
              id="volunteerDuration"
              name="volunteerDuration"
              placeHolder={content.howMuchTimePlaceholder}
              value={formik.values.volunteerDuration}
              onChangeHandler={formik.handleChange}
              error={
                formik.touched.volunteerDuration &&
                Boolean(formik.errors.volunteerDuration)
              }
              helperText={
                formik.touched.volunteerDuration &&
                formik.errors.volunteerDuration
              }
            />
          </Grid>
        </Grid>

        <Box mt={{ xs: 8, lg: 10 }} mb={2}>
          <TextArea
            placeholder={content.isThereAnyPlaceholder}
            name="additionalInformation"
            value={formik.values.additionalInformation}
            onChangeHandler={formik.handleChange}
            minRows={10}
          />
        </Box>

        <Box component="div" className={classes.checkboxContainer}>
          <Checkbox
            inputProps={{
              'aria-label': content.checkboxText,
            }}
            id="storeInformation"
            value={formik.values.storeInformation}
            onChange={() => {
              formik.values.storeInformation = !formik.values.storeInformation
            }}
          />
          <Typography className={classes.checkBoxDesc}>
            {content.checkboxText}
          </Typography>
        </Box>
        <ButtonComponent
          type="contained"
          text={content.button.content}
          gradient
          className={classes.recommendBtn}
          wrapperClass={classes.wrapperClass}
          onClick={formik.handleSubmit}
        />
      </form>
    </div>
  )
}

export default VolunteerForm
